/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { ObjectExtensions } from './object-extensions';

/**
 * Represents an array which is defined with at least one value.
 */
export type NonEmptyArray<T> = [T, ...T[]];

/**
 * Namespace which extends Array functionality.
 */
export namespace ArrayExtensions {

    /**
     * Verifies whether the object array has elements.
     * @param value - The array to examine.
     * @returns True if the object has at least one element in the array, false otherwise (if array is not defined or empty).
     */
    export function hasElements<T>(value: NonEmptyArray<T> | T[] | null | undefined): value is NonEmptyArray<T> {
        const emptyArrayLength = 0;
        return !ObjectExtensions.isNullOrUndefined(value) && value.length > emptyArrayLength;
    }

    /**
     * Retrieves the first element in the array or default value if the array is empty.
     * @param values - The array with values.
     * @param defaultValue - Optional parameter to specify default value. If not specified, undefined will be used.
     * @returns First element or default value.
     */
    export function firstOrDefault<T>(values: NonEmptyArray<T> | T[] | null | undefined, defaultValue?: T): T | undefined {
        if (hasElements(values)) {
            return values[0];
        }
        return defaultValue;
    }

    /**
     * Remove duplicate elements in the array.
     * @param value - The array to examine.
     * @returns An array with only unique elements.
     */
    export function unique<T>(value: T[]): T[] {
        if (!hasElements(value)) {
            return [];
        }

        return Array.from(new Set(value));
    }

    /**
     * Checks if all elements satisfy given condition.
     * @param value - The array to examine.
     * @param predicate - The function which checks an element.
     * @returns True if the array is empty or all the inner elements satisfy given condition,
     * false if at least one element does not pass the condition.
     */
    export function all<T>(value: T[] | null | undefined, predicate: (value: T, index: number, array: T[]) => boolean): boolean {
        if (!hasElements(value)) {
            return true;
        }

        return value.filter(predicate).length === value.length;
    }

    /**
     * Filters out null and undefined values from the given array.
     * @param value - The array with possibly undefined/null items.
     * @returns All items which are not null or undefined.
     */
    export function validValues<T>(value: (T | undefined | null)[] | undefined | null): T[] {
        if (!hasElements(value)) {
            return [];
        }

        return value.filter(item => !ObjectExtensions.isNullOrUndefined(item)) as T[];
    }

    /**
     * Merges items from 2-dimensional array into one array with all items.
     * @param value - The 2-dimensional array with the items.
     * @returns All items retrieved from all inner arrays.
     */
    export function flatten<T>(value: ((T | undefined | null)[] | undefined | null)[]): T[] {
        let result: T[] = [];

        if (!hasElements(value)) {
            return result;
        }

        for (const arrayLine of value) {
            if (!ObjectExtensions.isNullOrUndefined(arrayLine)) {
                result = result.concat(validValues(arrayLine));
            }
        }

        return result;
    }

    /**
     * Check if two arrays are equal.
     * @param firstArray - The array with the items.
     * @param secondArray - Another array with the items.
     * @param comparator - The comparator which compares the values and returns true if two objects are equal, false otherwise.
     * @returns True if arrays have the same items, false otherwise.
     */
    export function equals<T>(firstArray: T[], secondArray: T[], comparator?: (valueFromFirstArray: T, valueFromSecondArray: T) => boolean): boolean {
        if (firstArray.length !== secondArray.length) {
            return false;
        }

        if (comparator === undefined) {
            return JSON.stringify(firstArray) === JSON.stringify(secondArray);
        }

        // eslint-disable-next-line unicorn/no-for-loop -- Disable as we need to compare by indices.
        for (let index = 0; index < firstArray.length; ++index) {
            if (!comparator(firstArray[index], secondArray[index])) {
                return false;
            }
        }

        return true;
    }

    /**
     * Check if two arrays have the same elements irrespective of the order.
     * @param firstArray - The array with the items.
     * @param secondArray - Another array with the items.
     * @param comparator - The comparator which compares the values and returns true if two objects are equal, false otherwise.
     * @returns True if arrays have the same items, false otherwise.
     */
    export function haveSameItems<T>(
        firstArray: T[], secondArray: T[],
        comparator: (valueFromFirstArray: T, valueFromSecondArray: T) => boolean = (
            valueFromFirstArray: T, valueFromSecondArray: T) => valueFromFirstArray === valueFromSecondArray): boolean {

        if (firstArray.length !== secondArray.length) {
            return false;
        }

        for (const item of firstArray) {
            if (!secondArray.some(otherItem => comparator(item, otherItem))) {
                return false;
            }
        }

        return true;
    }

    /**
     * Calculates a sum of elements in the given array.
     * @param data - List of items.
     * @returns Sum of elements.
     */
    export function calculateSum(data: NonEmptyArray<number>): number {
        let result = data[0];
        for (let index = 1; index < data.length; ++index) {
            result += data[index];
        }

        return result;
    }

    /**
     * Reverses the elements in the array without modifying the original one.
     * @param array - The array with the items.
     * @returns A new array with reversed order.
     */
    export function reverse<T>(array: T[]): T[] {
        const newArray: T[] = [];

        for (let index = array.length - 1; index >= 0; --index) {
            newArray.push(array[index]);
        }

        return newArray;
    }
}
